import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import {
  Bot,
  Database,
  LoaderCircle,
  Menu,
  MessageSquareText,
  Plus,
  Search,
  Send,
  Sparkles,
  Trash2,
  User,
  X,
} from "lucide-react";
import Apiurl from "../environment/Apiurl";
import "../../assets/css/Chat.css";

const QUICK_PROMPTS = [
  "Show today's appointment summary",
  "Give me the current medicine stock summary",
  "Show today's staff attendance summary",
  "Show today's sale summary",
];

const Chat = () => {
  const userId =
    localStorage.getItem("user_id") ||
    localStorage.getItem("username") ||
    "crm-user";

  const displayName =
    localStorage.getItem("name") ||
    localStorage.getItem("username") ||
    "User";

  const activeConversationStorageKey = useMemo(
    () => `fourhealth-ai-active-conversation-${userId}`,
    [userId],
  );

  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [activeConversationTitle, setActiveConversationTitle] = useState("New Chat");
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [historySearch, setHistorySearch] = useState("");
  const [isHistoryLoading, setIsHistoryLoading] = useState(true);
  const [isConversationLoading, setIsConversationLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState("");
  const [isHistoryDrawerOpen, setIsHistoryDrawerOpen] = useState(false);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const normalizeMessages = (items = []) =>
    items.map((item, index) => ({
      id: `${item.timestamp || "message"}-${index}-${item.sender}`,
      sender: item.sender,
      text: item.text,
      timestamp: item.timestamp || new Date().toISOString(),
    }));

  const formatMessageTime = (value) => {
    const date = value ? new Date(value) : new Date();
    return date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatHistoryTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    const now = new Date();

    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
    });
  };

  const getHistoryGroup = (value) => {
    const date = new Date(value || Date.now());
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const targetStart = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );
    const differenceInDays = Math.floor(
      (todayStart.getTime() - targetStart.getTime()) / 86400000,
    );

    if (differenceInDays <= 0) return "Today";
    if (differenceInDays === 1) return "Yesterday";
    if (differenceInDays <= 7) return "Previous 7 Days";
    return "Older";
  };

  const filteredConversations = useMemo(() => {
    const query = historySearch.trim().toLowerCase();
    if (!query) return conversations;

    return conversations.filter((conversation) =>
      String(conversation.title || "")
        .toLowerCase()
        .includes(query),
    );
  }, [conversations, historySearch]);

  const groupedConversations = useMemo(() => {
    const groups = {
      Today: [],
      Yesterday: [],
      "Previous 7 Days": [],
      Older: [],
    };

    filteredConversations.forEach((conversation) => {
      const groupName = getHistoryGroup(conversation.updatedAt);
      groups[groupName].push(conversation);
    });

    return groups;
  }, [filteredConversations]);

  const loadConversation = async (conversationId, options = {}) => {
    if (!conversationId) return;

    const { closeDrawer = true } = options;
    setIsConversationLoading(true);
    setError("");

    try {
      const response = await axios.get(
        `${Apiurl}/chat_history/${encodeURIComponent(userId)}/${conversationId}`,
      );

      const conversation = response?.data?.conversation;
      if (!conversation) {
        throw new Error("Conversation data was empty");
      }

      setActiveConversationId(conversation._id);
      setActiveConversationTitle(conversation.title || "New Chat");
      setMessages(normalizeMessages(conversation.messages || []));
      localStorage.setItem(activeConversationStorageKey, conversation._id);

      if (closeDrawer) {
        setIsHistoryDrawerOpen(false);
      }
    } catch (requestError) {
      console.error("Unable to load chat conversation:", requestError);
      setError(
        requestError?.response?.data?.error ||
          "Unable to load this previous conversation.",
      );
    } finally {
      setIsConversationLoading(false);
    }
  };

  const loadConversations = async ({ openSavedConversation = false } = {}) => {
    setIsHistoryLoading(true);

    try {
      const response = await axios.get(
        `${Apiurl}/chat_history/${encodeURIComponent(userId)}`,
      );
      const history = Array.isArray(response?.data?.conversations)
        ? response.data.conversations
        : [];

      setConversations(history);

      if (openSavedConversation && history.length > 0) {
        const savedConversationId = localStorage.getItem(
          activeConversationStorageKey,
        );
        const savedConversationExists = history.some(
          (item) => String(item._id) === String(savedConversationId),
        );

        if (savedConversationId && savedConversationExists) {
          await loadConversation(savedConversationId, { closeDrawer: false });
        }
      }
    } catch (requestError) {
      console.error("Unable to load chat history:", requestError);
      setError(
        requestError?.response?.data?.error ||
          "Unable to load previous chat history.",
      );
      setConversations([]);
    } finally {
      setIsHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadConversations({ openSavedConversation: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isSending]);

  const startNewChat = () => {
    if (isSending) return;

    setActiveConversationId(null);
    setActiveConversationTitle("New Chat");
    setMessages([]);
    setMessage("");
    setError("");
    localStorage.removeItem(activeConversationStorageKey);
    setIsHistoryDrawerOpen(false);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const sendMessage = async (textOverride) => {
    const cleanMessage = String(textOverride ?? message).trim();
    if (!cleanMessage || isSending || isConversationLoading) return;

    const userMessage = {
      id: `${Date.now()}-user`,
      sender: "user",
      text: cleanMessage,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setMessage("");
    setError("");
    setIsSending(true);

    try {
      const response = await axios.post(`${Apiurl}/chat_multi_table`, {
        message: cleanMessage,
        userId,
        conversationId: activeConversationId || undefined,
      });

      const reply = response?.data?.reply;
      const returnedConversationId = response?.data?.conversationId;
      const returnedTitle = response?.data?.title || cleanMessage;
      const returnedUpdatedAt =
        response?.data?.updatedAt || new Date().toISOString();

      if (!reply) {
        throw new Error("AI reply was empty");
      }

      const modelMessage = {
        id: `${Date.now()}-model`,
        sender: "model",
        text: reply,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, modelMessage]);

      if (returnedConversationId) {
        setActiveConversationId(returnedConversationId);
        setActiveConversationTitle(returnedTitle || "New Chat");
        localStorage.setItem(
          activeConversationStorageKey,
          returnedConversationId,
        );

        setConversations((prev) => {
          const withoutCurrent = prev.filter(
            (item) => String(item._id) !== String(returnedConversationId),
          );

          return [
            {
              _id: returnedConversationId,
              title: returnedTitle || "New Chat",
              updatedAt: returnedUpdatedAt,
              messageCount:
                messages.length + 2,
            },
            ...withoutCurrent,
          ];
        });
      }

      // Sync metadata/order with the backend after every successful response.
      loadConversations();
    } catch (requestError) {
      console.error("CRM AI chat error:", requestError);
      const backendMessage =
        requestError?.response?.data?.error ||
        requestError?.response?.data?.message ||
        "Unable to get an AI response right now.";

      setError(backendMessage);
      setMessages((prev) => [
        ...prev,
        {
          id: `${Date.now()}-error`,
          sender: "model",
          text: `Sorry, I couldn't complete that request. ${backendMessage}`,
          timestamp: new Date().toISOString(),
          isError: true,
        },
      ]);
    } finally {
      setIsSending(false);
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  };

  const deleteConversation = async (event, conversation) => {
    event.stopPropagation();

    const shouldDelete = window.confirm(
      `Delete "${conversation.title || "this chat"}" permanently?`,
    );
    if (!shouldDelete) return;

    try {
      await axios.delete(
        `${Apiurl}/chat_history/${encodeURIComponent(userId)}/${conversation._id}`,
      );

      setConversations((prev) =>
        prev.filter((item) => String(item._id) !== String(conversation._id)),
      );

      if (String(activeConversationId) === String(conversation._id)) {
        startNewChat();
      }
    } catch (requestError) {
      console.error("Unable to delete chat conversation:", requestError);
      setError(
        requestError?.response?.data?.error ||
          "Unable to delete this conversation.",
      );
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendMessage();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const renderHistoryGroups = () => {
    const groupOrder = ["Today", "Yesterday", "Previous 7 Days", "Older"];
    const hasHistory = filteredConversations.length > 0;

    if (isHistoryLoading) {
      return (
        <div className="crm-chat-history-state">
          <LoaderCircle size={17} className="crm-chat-spin" />
          <span>Loading chats...</span>
        </div>
      );
    }

    if (!hasHistory) {
      return (
        <div className="crm-chat-history-empty">
          <MessageSquareText size={20} />
          <span>
            {historySearch.trim() ? "No matching chats" : "No previous chats yet"}
          </span>
        </div>
      );
    }

    return groupOrder.map((groupName) => {
      const groupItems = groupedConversations[groupName];
      if (!groupItems.length) return null;

      return (
        <div className="crm-chat-history-group" key={groupName}>
          <div className="crm-chat-history-label">{groupName}</div>
          <div className="crm-chat-history-list">
            {groupItems.map((conversation) => (
              <button
                type="button"
                key={conversation._id}
                className={`crm-chat-history-item ${
                  String(activeConversationId) === String(conversation._id)
                    ? "is-active"
                    : ""
                }`}
                onClick={() => loadConversation(conversation._id)}
              >
                <MessageSquareText size={15} className="crm-chat-history-icon" />
                <span className="crm-chat-history-item-main">
                  <span className="crm-chat-history-title">
                    {conversation.title || "New Chat"}
                  </span>
                  <span className="crm-chat-history-time">
                    {formatHistoryTime(conversation.updatedAt)}
                  </span>
                </span>
                <span
                  role="button"
                  tabIndex={0}
                  className="crm-chat-history-delete"
                  title="Delete conversation"
                  onClick={(event) => deleteConversation(event, conversation)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      deleteConversation(event, conversation);
                    }
                  }}
                >
                  <Trash2 size={14} />
                </span>
              </button>
            ))}
          </div>
        </div>
      );
    });
  };

  return (
    <div className="crm-chat-page">
      {isHistoryDrawerOpen && (
        <button
          type="button"
          className="crm-chat-mobile-backdrop"
          aria-label="Close chat history"
          onClick={() => setIsHistoryDrawerOpen(false)}
        />
      )}

      <div className="crm-chat-layout">
        <aside
          className={`crm-chat-history-panel ${
            isHistoryDrawerOpen ? "is-mobile-open" : ""
          }`}
        >
          <div className="crm-chat-history-top">
            <div className="crm-chat-history-brand">
              <div className="crm-chat-history-brand-icon">
                <Bot size={19} />
              </div>
              <div>
                <strong>4Health AI</strong>
                <span>CRM Assistant</span>
              </div>
            </div>

            <button
              type="button"
              className="crm-chat-mobile-close"
              onClick={() => setIsHistoryDrawerOpen(false)}
              aria-label="Close history"
            >
              <X size={19} />
            </button>
          </div>

          <button
            type="button"
            className="crm-chat-new-btn"
            onClick={startNewChat}
            disabled={isSending}
          >
            <Plus size={17} />
            <span>New Chat</span>
          </button>

          <div className="crm-chat-history-search">
            <Search size={15} />
            <input
              type="text"
              value={historySearch}
              onChange={(event) => setHistorySearch(event.target.value)}
              placeholder="Search chats"
              aria-label="Search previous chats"
            />
            {historySearch && (
              <button
                type="button"
                onClick={() => setHistorySearch("")}
                aria-label="Clear chat search"
              >
                <X size={14} />
              </button>
            )}
          </div>

          <div className="crm-chat-history-scroll">{renderHistoryGroups()}</div>

          <div className="crm-chat-history-footer">
            <div className="crm-chat-history-user-avatar">
              <User size={15} />
            </div>
            <div>
              <strong>{displayName}</strong>
              <span>CRM User</span>
            </div>
          </div>
        </aside>

        <main className="crm-chat-main">
          <header className="crm-chat-main-header">
            <div className="crm-chat-main-header-left">
              <button
                type="button"
                className="crm-chat-history-toggle"
                onClick={() => setIsHistoryDrawerOpen(true)}
                aria-label="Open chat history"
              >
                <Menu size={19} />
              </button>

              <div>
                <div className="crm-chat-main-kicker">
                  <Sparkles size={12} />
                  Live CRM Intelligence
                </div>
                <h5>{activeConversationTitle || "New Chat"}</h5>
              </div>
            </div>

            <div className="crm-chat-live-chip">
              <span className="crm-chat-live-dot" />
              <Database size={13} />
              <span>Live</span>
            </div>
          </header>

          <div className="crm-chat-conversation">
            {isConversationLoading ? (
              <div className="crm-chat-center-state">
                <LoaderCircle size={24} className="crm-chat-spin" />
                <span>Opening conversation...</span>
              </div>
            ) : messages.length === 0 ? (
              <div className="crm-chat-welcome">
                <div className="crm-chat-welcome-logo">
                  <Bot size={30} />
                </div>
                <h3>How can I help with your CRM?</h3>
                <p>
                  Ask about appointments, patients, pharmacy stock, attendance,
                  sales, billing, expenses and other connected 4Health data.
                </p>

                <div className="crm-chat-prompt-grid">
                  {QUICK_PROMPTS.map((prompt) => (
                    <button
                      type="button"
                      key={prompt}
                      onClick={() => sendMessage(prompt)}
                      disabled={isSending}
                    >
                      <Sparkles size={14} />
                      <span>{prompt}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="crm-chat-message-stack">
                {messages.map((item) => (
                  <div
                    key={item.id}
                    className={`crm-chat-message ${
                      item.sender === "user" ? "is-user" : "is-model"
                    }`}
                  >
                    <div
                      className={`crm-chat-message-avatar ${
                        item.sender === "user" ? "is-user" : "is-model"
                      }`}
                    >
                      {item.sender === "user" ? (
                        <User size={15} />
                      ) : (
                        <Bot size={16} />
                      )}
                    </div>

                    <div className="crm-chat-message-body">
                      <div className="crm-chat-message-name">
                        {item.sender === "user" ? "You" : "4Health AI"}
                        <span>{formatMessageTime(item.timestamp)}</span>
                      </div>

                      <div
                        className={`crm-chat-message-text ${
                          item.sender === "user" ? "is-user" : "is-model"
                        } ${item.isError ? "is-error" : ""}`}
                      >
                        {item.sender === "model" ? (
                          <ReactMarkdown>{item.text}</ReactMarkdown>
                        ) : (
                          item.text
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {isSending && (
                  <div className="crm-chat-message is-model">
                    <div className="crm-chat-message-avatar is-model">
                      <Bot size={16} />
                    </div>
                    <div className="crm-chat-message-body">
                      <div className="crm-chat-message-name">4Health AI</div>
                      <div className="crm-chat-thinking">
                        <LoaderCircle size={16} className="crm-chat-spin" />
                        <span>Checking CRM data...</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="crm-chat-composer-wrap">
            {error && <div className="crm-chat-error">{error}</div>}

            <form className="crm-chat-composer" onSubmit={handleSubmit}>
              <textarea
                ref={textareaRef}
                rows={1}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Message 4Health AI..."
                disabled={isSending || isConversationLoading}
                aria-label="Message 4Health AI"
              />
              <button
                type="submit"
                disabled={
                  !message.trim() || isSending || isConversationLoading
                }
                title="Send message"
              >
                {isSending ? (
                  <LoaderCircle size={18} className="crm-chat-spin" />
                ) : (
                  <Send size={18} />
                )}
              </button>
            </form>
            <div className="crm-chat-composer-note">
              Enter to send · Shift + Enter for a new line
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Chat;